import React, { useCallback, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { FileIcon } from '../../components/attachments/FileIcon';
import { formatFileSize } from '../../components/attachments/fileIconUtils';
import {
  Button,
  CheckCircleIcon,
  ChevronLeftIcon,
  SpinnerIcon,
  TrashIcon,
  UploadCloudIcon,
  XIcon,
} from '../../components/ui';
import { useTheme } from '../../contexts/ThemeContext';
import { useNavigateBack, getLocationReturnPath } from '../../hooks/useNavigateBack';
import { cn } from '../../lib/utils';
import { bulkImportDocuments } from '../../services/mutations';

const ACCEPT = 'image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv';
const MAX_BYTES = 15 * 1024 * 1024;
const MAX_FILES = 20;

type ImportResult = {
  created: Array<{ id: number; title: string }>;
  errors: Array<{ filename: string; message: string }>;
};

function errMsg(e: unknown): string {
  const msg = (e as any)?.response?.data?.message || (e as any)?.message || 'Something went wrong';
  return Array.isArray(msg) ? msg.join(', ') : String(msg);
}

function fileKey(file: File) {
  return `${file.name}:${file.size}:${file.lastModified}`;
}

export const Import: React.FC = () => {
  const { colors } = useTheme();
  const location = useLocation();
  const goBack = useNavigateBack('/documents', getLocationReturnPath(location.state));
  const inputRef = useRef<HTMLInputElement>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);

  const addFiles = useCallback((incoming: FileList | File[]) => {
    const next = Array.from(incoming);
    if (!next.length) return;
    setError(null);
    setResult(null);
    setFiles((prev) => {
      const seen = new Set(prev.map(fileKey));
      const merged = [...prev];
      for (const file of next) {
        if (file.size > MAX_BYTES) continue;
        const key = fileKey(file);
        if (seen.has(key)) continue;
        if (merged.length >= MAX_FILES) break;
        seen.add(key);
        merged.push(file);
      }
      return merged;
    });
    const oversized = next.filter((f) => f.size > MAX_BYTES);
    if (oversized.length) {
      setError(`${oversized.length} file(s) exceed the 15 MB limit and were skipped.`);
    }
  }, []);

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
    setResult(null);
  };

  const clearAll = () => {
    setFiles([]);
    setResult(null);
    setError(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };
  const onDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    if (e.dataTransfer.types.includes('Files')) setDragActive(true);
  };
  const onDragLeave = (e: React.DragEvent) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDragActive(false);
  };
  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files);
  };

  const upload = async () => {
    if (!files.length) {
      setError('Select at least one file to import.');
      return;
    }
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const data = new FormData();
      for (const file of files) data.append('files', file);
      const response = await bulkImportDocuments(data);
      setResult(response);
      if (response.created.length) {
        setFiles([]);
        if (inputRef.current) inputRef.current.value = '';
      }
    } catch (e) {
      setError(errMsg(e) || 'Failed to import documents');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-4 md:p-6">
      <div className={cn('mx-auto max-w-3xl overflow-hidden rounded-2xl border shadow-sm', colors.surface, colors.border)}>
        <div className={cn('flex flex-wrap items-start justify-between gap-3 border-b px-4 py-4 sm:px-5', colors.border)}>
          <div className="flex min-w-0 items-start gap-2">
            <Button type="button" variant="ghost" size="icon" onClick={goBack} aria-label="Back" title="Back" className="mt-0.5 shrink-0">
              <ChevronLeftIcon className="h-5 w-5" />
            </Button>
            <div className="min-w-0">
              <h2 className={cn('text-base font-semibold', colors.text)}>Bulk import</h2>
              <p className={cn('mt-0.5 text-sm', colors.textMuted)}>
                Upload up to {MAX_FILES} files as draft documents. No AI scan — open each draft later to process.
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-4 px-4 py-5 sm:px-5">
          <div
            onDragOver={onDragOver}
            onDragEnter={onDragEnter}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            className={cn(
              'flex flex-col items-center justify-center rounded-2xl border-2 border-dashed px-4 py-10 text-center transition',
              dragActive
                ? 'border-indigo-400 bg-indigo-50/60 dark:border-indigo-500 dark:bg-indigo-950/30'
                : cn(colors.border, 'bg-slate-50/70 dark:bg-slate-900/40'),
            )}
          >
            <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-600 dark:bg-indigo-950/50 dark:text-indigo-300">
              <UploadCloudIcon className="h-6 w-6" />
            </div>
            <p className={cn('text-sm font-medium', colors.text)}>Drop files here, or browse</p>
            <p className={cn('mt-1 text-xs', colors.textMuted)}>
              PDF, Word, Excel, CSV, or images · max 15 MB each · up to {MAX_FILES} files
            </p>
            <button
              type="button"
              disabled={busy}
              onClick={() => inputRef.current?.click()}
              className={cn(
                'mt-4 inline-flex items-center gap-1.5 rounded-xl border px-3.5 py-2 text-sm font-medium',
                colors.border,
                colors.text,
              )}
            >
              Choose files
            </button>
            <input
              ref={inputRef}
              type="file"
              accept={ACCEPT}
              multiple
              className="hidden"
              onChange={(e) => {
                if (e.target.files?.length) addFiles(e.target.files);
                e.target.value = '';
              }}
            />
          </div>

          {files.length > 0 && (
            <div className={cn('overflow-hidden rounded-xl border', colors.border)}>
              <div className={cn('flex items-center justify-between border-b px-3 py-2', colors.border)}>
                <p className={cn('text-xs font-medium', colors.textMuted)}>
                  {files.length} file{files.length === 1 ? '' : 's'} ready
                </p>
                <button
                  type="button"
                  disabled={busy}
                  onClick={clearAll}
                  className={cn('text-xs font-medium hover:underline', colors.textSecondary)}
                >
                  Clear all
                </button>
              </div>
              <ul className="max-h-72 divide-y overflow-y-auto dark:divide-slate-800">
                {files.map((file, index) => (
                  <li key={fileKey(file)} className={cn('flex items-center gap-3 px-3 py-2.5', colors.border)}>
                    <FileIcon fileName={file.name} size="sm" />
                    <div className="min-w-0 flex-1">
                      <p className={cn('truncate text-sm font-medium', colors.text)}>{file.name}</p>
                      <p className={cn('text-xs', colors.textMuted)}>{formatFileSize(file.size)}</p>
                    </div>
                    <button
                      type="button"
                      disabled={busy}
                      title="Remove"
                      aria-label={`Remove ${file.name}`}
                      onClick={() => removeFile(index)}
                      className={cn(
                        'inline-flex h-8 w-8 items-center justify-center rounded-lg border transition hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950/40 dark:hover:text-red-400',
                        colors.border,
                        colors.textMuted,
                      )}
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {error && (
            <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-300">
              {error}
            </div>
          )}

          {result && (
            <div className="space-y-2">
              {result.created.length > 0 && (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800 dark:border-emerald-900/50 dark:bg-emerald-950/40 dark:text-emerald-300">
                  <div className="flex items-center gap-2 font-medium">
                    <CheckCircleIcon className="h-4 w-4" />
                    Imported {result.created.length} draft
                    {result.created.length === 1 ? '' : 's'}
                  </div>
                </div>
              )}
              {result.errors.length > 0 && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/40 dark:text-amber-200">
                  <p className="font-medium">{result.errors.length} file(s) failed</p>
                  <ul className="mt-1 space-y-0.5 text-xs">
                    {result.errors.map((item) => (
                      <li key={`${item.filename}:${item.message}`}>
                        <span className="font-medium">{item.filename}</span> — {item.message}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {result.created.length > 0 && (
                <Button type="button" variant="default" size="sm" onClick={goBack} className="gap-1.5">
                  Back to documents
                </Button>
              )}
            </div>
          )}

          <div className="flex flex-wrap items-center justify-end gap-2 pt-1">
            {files.length > 0 && !result?.created.length && (
              <button
                type="button"
                disabled={busy}
                onClick={clearAll}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-xl border px-3.5 py-2 text-sm font-medium',
                  colors.border,
                  colors.text,
                )}
              >
                <XIcon className="h-4 w-4" />
                Cancel
              </button>
            )}
            <button
              type="button"
              disabled={busy || files.length === 0}
              onClick={upload}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-xl px-3.5 py-2 text-sm font-medium text-white shadow-sm disabled:cursor-not-allowed disabled:opacity-50',
                colors.primary,
              )}
            >
              {busy ? <SpinnerIcon className="h-4 w-4" /> : <UploadCloudIcon className="h-4 w-4" />}
              {busy ? 'Uploading…' : `Upload ${files.length || ''} draft${files.length === 1 ? '' : 's'}`.trim()}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
